import { ISpace } from '@konova/api-types';
import { isWithinInterval } from 'date-fns';
import DOMPurify from 'dompurify';
import * as H from 'history';
import i18n from 'i18next';
import { BACKEND_URL, FALLBACK_LANGUAGE } from './constants';
import { IResourceQuery, ITableState, IUser } from './types';
import { AxiosInstance } from 'axios';

function processResponse(response: any) {
  const statusCode = response.status;
  const contentType = response.headers.get('content-type');
  const contentDisposition = response.headers.get('content-disposition');
  const data =
    contentType && contentType.indexOf('application/json') !== -1 ? response.json() : contentDisposition ? response.blob() : response.text();
  return Promise.all([statusCode, data, contentDisposition]).then(res => {
    const statusCode = res[0];
    const body = res[1] || undefined;
    const response = { statusCode, body, contentDisposition };
    if (statusCode >= 200 && statusCode < 300) return response;
    else throw response;
  });
}

export function makeAsyncCall<T = any>(options: {
  method: string;
  body?: any;
  url: string;
  headers?: object;
  accept?: string;
  axios?: AxiosInstance;
}): Promise<T> {
  const { method, body, url, headers, accept } = options;
  const requestHeaders: HeadersInit = new Headers();
  requestHeaders.set('Content-Type', accept ?? 'application/json');
  requestHeaders.set('Accept', 'application/json');
  if (i18n.languages && i18n.languages[0]) {
    requestHeaders.set('Accept-Language', encodeURI(i18n.languages[0]));
  } else {
    requestHeaders.set('Accept-Language', encodeURI(FALLBACK_LANGUAGE));
  }

  if (headers) {
    Object.keys(headers).forEach((key: string) => {
      if (headers[key] === null) {
        requestHeaders.delete(key);
      } else {
        requestHeaders.set(key, headers[key]);
      }
    });
  }

  if (options.axios) {
    return options.axios.request({
      url,
      method,
      headers: {
        'Accept-Language': requestHeaders.get('Accept-Language'),
        'Content-Type': body instanceof FormData ? 'multipart/form-data' : 'application/json'
      },
      data: body,
      ...(url.match('^' + BACKEND_URL) ? { credentials: 'include' } : {}),
    }).then(response => {
      return { statusCode: response.status, body: response.data, contentDisposition: response.headers['content-disposition'] } as T;
    });
  }

  return fetch(url, {
    method,
    headers: requestHeaders,
    body,
    ...(url.match('^' + BACKEND_URL) ? { credentials: 'include' } : {}),
  }).then((res: any) => {
    return processResponse(res) as T;
  });
}

export const createFilterParamsString = (query: IResourceQuery, url = '') => {
  let urlWithParams = `${url}?`;
  const filter = query.filter || {};

  for (const filterParameter in filter) {
    if (filter[filterParameter])
      urlWithParams += `filter[${filterParameter}]=${filter[filterParameter]}&`;
  }

  if (query.survey_tag_ids && Array.isArray(query.survey_tag_ids)) {
    query.survey_tag_ids.forEach((tag_id: number) => {
      urlWithParams += `survey_tag_ids[]=${tag_id}&`;
    });
  }

  if (query.name) {
    urlWithParams += `name=${query.name}&`;
  }

  if (query.sort) urlWithParams += `sort=${query.sort}&`;
  if (query.page) urlWithParams += `page=${query.page}&`;
  if (query.per_page) urlWithParams += `per_page=${query.per_page}&`;
  if (query.include) urlWithParams += `include=${query.include}&`;
  if (query.group_by) urlWithParams += `group_by=${query.group_by}&`;
  if (query.append) urlWithParams += `append=${query.append}&`;
  if (query.random_seed) urlWithParams += `random_seed=${query.random_seed}&`;
  if (query.context) urlWithParams += `context=${query.context}&`;

  return urlWithParams.slice(0, -1);
};

export const convertQueryParamsStateToQueryObject = (
  queryParams,
  extra?: { filter?: object; include?: string }
) => {
  const filters = {};
  for (const filterKey in queryParams.filters) {
    filters[filterKey] = queryParams.filters[filterKey].filterVal;
  }
  return {
    ...extra,
    filter: { ...(extra?.filter || {}), ...filters },
    sort: queryParams.sortField ? (queryParams.sortOrder !== 'asc' ? '-' : '') + queryParams.sortField : 'id',
  };
};

const extractFilters = (filters: { [key: string]: any }) => {
  const resultFilters: { [key: string]: any } = {};
  const questionFilters: { [key: string]: any } = {};

  for (const filterKey in filters) {
    if (filterKey === 'questions') {
      Object.keys(filters[filterKey]).forEach(qKey => {
        questionFilters[qKey] = filters[filterKey][qKey].filterVal;
      });
    } else if (filters[filterKey].filterVal) {
      resultFilters[filterKey] = filters[filterKey].filterVal;
    }
  }

  return { resultFilters, questionFilters };
};

export const convertTableStateToCustomQueryObject = (
  tableState: ITableState,
  extra?: { filter?: object; include?: string }
): {
  filter: { [key: string]: any };
  sort: string;
  page: number;
  perPage: number;
} => {
  const { resultFilters, questionFilters } = extractFilters(tableState.filters);

  const result: {
    filter: { [key: string]: any; questions?: { [key: string]: any } };
    sort: string;
    page: number;
    perPage: number;
  } = {
    ...extra,
    filter: { ...(extra?.filter || {}), ...resultFilters },
    sort: tableState.sortField ? (tableState.sortOrder !== 'asc' ? '-' : '') + tableState.sortField : 'id',
    page: tableState.page,
    perPage: tableState.sizePerPage,
  };

  if (Object.keys(questionFilters).length > 0) {
    result.filter.questions = questionFilters;
  }

  return result;
};

export const convertTableStateToQueryObject = (
  tableState: ITableState,
  extra?: { filter?: object; include?: string }
) => {
  const filters = {};
  for (const filterKey in tableState.filters) {
    filters[filterKey] = tableState.filters[filterKey].filterVal;
  }
  return {
    ...extra,
    filter: { ...(extra?.filter || {}), ...filters },
    sort: tableState.sortField ? (tableState.sortOrder !== 'asc' ? '-' : '') + tableState.sortField : 'id',
    page: tableState.page,
    per_page: tableState.sizePerPage,
  };
};

export const getSalutation = () => {
  const hour = new Date().getHours();
  let salutation = i18n.t('common.salutation-informal');
  if (hour < 10) {
    salutation = i18n.t('common.good-morning');
  }
  if (hour >= 17) {
    salutation = i18n.t('common.good-evening');
  }
  return salutation;
};

export const getStageColor = stage => {
  const now = new Date();
  const start = new Date(stage.date_start);
  const end = new Date(stage.date_end);

  if (isWithinInterval(now, { start, end })) {
    return '#44AE75';
  } else {
    return '#c1664f';
  }
};

export const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

export const nl2br = (subject: string) => {
  if (!subject) return '';
  if (/^<\w+(\s[^>]*)?>/i.test(subject)) return subject;
  return subject.replace(/\n/g, '<br/>');
};

export const slugify = (text: string) => {
  const from = 'ãàáäâẽèéëêìíïîõòóöôùúüûñç·/_,:;';
  const to = 'aaaaaeeeeeiiiiooooouuuunc------';

  if (text === null) {
    text = (Math.random() + 1).toString(36).substring(1);
  }

  text = text.toLowerCase();

  for (let i = 0, l = from.length; i < l; i++) {
    text = text.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
  }

  return text
    .toString() // Cast to string
    .trim() // Remove whitespace from both sides of a string
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/&/g, '-y-') // Replace & with 'and'
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/--+/g, '-'); // Replace multiple - with single -
};

export const fileToBas64Url = (file, callBack) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => {
    callBack(reader.result);
  };
};

/**
 * @deprecated use `getCharactersUntilDelimiter`
 */
export const getPosition = (string, subString, index) =>
  string.split(subString, index).join(subString).length;

export const sanitize = (html: string) =>
  DOMPurify.sanitize(html, { ADD_TAGS: ['iframe'], ADD_ATTR: ['target', 'referrerPolicy'] });

export const sanitizeCssClassName = (name: string) => {
  return name
    .trim()
    .replace(/[^a-zA-Z0-9-]/g, '-')
    .replace(/-+/g, '-');
};

export const createWrapperForQuillVideos = () => {
  const videos = document.querySelectorAll('.ql-video');
  for (let i = 0; i < videos.length; i++) {
    const embedContainer = document.createElement('div');
    embedContainer.setAttribute('class', 'embed-container');
    const parent = videos[i].parentNode;
    parent?.insertBefore(embedContainer, videos[i]);
    embedContainer.appendChild(videos[i]);
  }
};

export const sortOptions = (options: any[], sortableValue: string = 'label') => {
  if (options)
    return options.sort((option1, option2) => {
      if (option1[sortableValue] !== null && option2[sortableValue] !== null) {
        return option1[sortableValue]
          .toLowerCase()
          .localeCompare(option2[sortableValue].toLowerCase());
      } else {
        return 0;
      }
    });
  return [];
};

export const generateDownloadUrl = (
  lang: string,
  surveySlug: string,
  surveyId?: number,
  participantGroupId?: number,
  stageId?: number
) => {
  let url = `${BACKEND_URL}surveys/general-report-download?language=${lang}&slug=${surveySlug}&filter[report_type]=participantGroupReport`;
  if (surveyId) {
    url += `&filter[survey_id]=${surveyId}`;
  }
  if (stageId) {
    url += `&filter[survey_stage_id]=${stageId}`;
  }
  if (participantGroupId) {
    url += `&filter[participant_group_id]=${participantGroupId}`;
  }
  return url;
};

export const isMobile = () =>
  /Android|webOS|iPhone|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

export const isTablet = () =>
  /(iPad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/i.test(
    navigator.userAgent
  );

export const replaceFavicons = (space: ISpace) => {
  const node = document.querySelector('head link[rel="shortcut icon"]');
  if (node) {
    node.setAttribute('href', space.allFaviconUrls['48x48']);
  }

  for (const size in space.allFaviconUrls) {
    if (space.allFaviconUrls.hasOwnProperty(size)) {
      const url = space.allFaviconUrls[size];
      const node = document.querySelector(`head link[rel="icon"][sizes="${size}"]`);
      if (node) {
        node.setAttribute('href', url);
      }
    }
  }
};

export const checkUserSalutation = (user: IUser) => {
  switch (user?.spaceUser?.salutation) {
    case 'Herr':
      return i18n.t('common.sir');
    case 'Frau':
      return i18n.t('common.madam');
    case 'Keine Angabe':
      return i18n.t('common.not-specified');
    default:
      return null;
  }
};